import { useMentionOptions } from "@/hooks/use-mention-options";
import { useImageRenderer } from "@/hooks/use-image-renderer";
import { useVideoRenderer } from "@/hooks/use-video-renderer";
import { useWikiPage } from "@/hooks/use-wiki-page";
import { useBreadcrumbTitle } from "@msuite/ui/app-shell";
import { BlankSlate } from "@msuite/ui/blank-slate";
import { Divider } from "@msuite/ui/divider";
import { icon } from "@msuite/ui/icons";
import { TiptapDisplay } from "@msuite/ui/libs/tiptap";
import { useTranslation } from "@msuite/ui/localization";
import { guardString } from "@msuite/utils/strings";
import { collectBlockAttrValues } from "@msuite/utils/tiptap";
import type { Nullable } from "@msuite/utils/types";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { WikiPageActions } from "./wiki-page-actions";
import { WikiPageByline } from "./wiki-page-byline";
import { WikiPageEditor } from "./wiki-page-editor";
import { WikiPageFiles } from "./wiki-page-files";
import { WikiPageLayout } from "./wiki-page-layout";
import { WikiPageSubPages } from "./wiki-page-sub-pages";
import { WikiPageThread } from "./wiki-page-thread";
import { WikiPageTrail } from "./wiki-page-trail";
import { WikiSpaceOverview } from "./wiki-space-overview";

/** Props Interface */
interface WikiPageProps {
	spaceId: Nullable<string>;
	pageId: Nullable<string>;
}

export function WikiPage({ spaceId, pageId }: WikiPageProps) {
	/** Hooks */
	const { t } = useTranslation();
	const navigate = useNavigate();
	const mentionsOptions = useMentionOptions();
	const videoRenderer = useVideoRenderer();
	const imageRenderer = useImageRenderer();
	const [searchParams] = useSearchParams();

	/** State */
	const [isEditing, setIsEditing] = useState(false);

	/** Query */
	const { wikiPage, isLoading } = useWikiPage({ spaceId, pageId });

	/** Effects */
	const isCreating = searchParams.get("create") === "true";
	const isEditRequested = searchParams.get("edit") === "true";
	useEffect(() => {
		setIsEditing(isCreating || isEditRequested);
	}, [pageId, isCreating, isEditRequested]);
	// The breadcrumb names the page after its title while it is read and while it is edited alike;
	// without it the last crumb shows the page's id.
	useBreadcrumbTitle(pageId, wikiPage ? guardString(wikiPage.title, t("wiki.untitled")) : null);

	/** Computed */
	const title = guardString(wikiPage?.title, t("wiki.untitled"));
	// The pictures the text embeds are the page's files too, but they are shown where they sit,
	// not listed beneath the page as attachments.
	const embeddedFileIds = collectBlockAttrValues(wikiPage?.content ?? "", "imageBlock", "fileId");

	/** Guard */
	if (!spaceId) {
		return (
			<BlankSlate
				label={t("wiki.select_space")}
				Icon={icon("BookOpen")}
				withBorder={false}
				withBackground={false}
				withAccessory={false}
			/>
		);
	}

	if (!pageId) {
		return <WikiSpaceOverview spaceId={spaceId} />;
	}

	if (isLoading) {
		return <BlankSlate isLoading />;
	}

	if (!wikiPage) {
		return <BlankSlate Icon={icon("FileText")} />;
	}

	/** Render */
	return (
		// The layout and the thread sit above the mode switch, so that opening or closing the
		// editor swaps only what is inside the column: the card, the thread, its scroll position
		// and the note somebody is drafting all stay mounted, and nothing beside the document
		// blinks when the mode changes.
		<WikiPageLayout
			fill={isEditing}
			aside={<WikiPageThread page={wikiPage} />}
		>
			{isEditing ? (
				<WikiPageEditor
					page={wikiPage}
					spaceId={spaceId}
					pageId={pageId}
					isCreate={isCreating}
					onClose={() => {
						setIsEditing(false);
						if (isCreating || isEditRequested) {
							navigate(`/wiki/${spaceId}/${pageId}`, { replace: true });
						}
					}}
				/>
			) : (
				<React.Fragment>
					<div className="flex min-h-8 flex-row items-center justify-between gap-4 -mx-4 -mt-4 p-4 border-b border-color">
						<WikiPageTrail
							spaceId={spaceId}
							pageId={pageId}
						/>
						<WikiPageActions
							spaceId={spaceId}
							pageId={pageId}
							onEdit={() => setIsEditing(true)}
						/>
					</div>
					{/* The document is its own scrollport, which is what keeps a long page inside the
					    column: the column is the panel's height, the trail sits on top of it, and this
					    takes whatever is left. Without the pair of them the document's own height is
					    what the column measures, and the panel scrolls instead — trail, thread and all. */}
					<div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
						<h1 className="mt-4 text-3xl font-semibold leading-tight tracking-tight">{title}</h1>
						<WikiPageByline
							page={wikiPage}
							className="mt-2.5"
						/>
						<Divider className="my-6" />
						<TiptapDisplay
							json={wikiPage.content}
							videoRenderer={videoRenderer}
							imageRenderer={imageRenderer}
							className="tiptap-document"
							{...mentionsOptions}
						/>
						<WikiPageFiles
							page={wikiPage}
							embeddedFileIds={embeddedFileIds}
						/>
						<WikiPageSubPages
							spaceId={spaceId}
							pageId={pageId}
						/>
					</div>
				</React.Fragment>
			)}
		</WikiPageLayout>
	);
}
